import type { InquiryListRequest } from '@ghp/api-client'
import { INQUIRY_LIST_DIRECTIONS, INQUIRY_LIST_SORTS } from '@ghp/domain'
import { z } from 'zod'

import { urlCount, urlText } from '../../../lib/search-params'
import { CLIENT_KIND_VALUES, INQUIRY_KIND_VALUES } from './inquiry-presentation'

/**
 * Le modèle des filtres de l'écran « Demandes » : le schéma des `search params`, les vues, et la
 * **couture** vers la requête serveur. Hors de la route pour que le composant d'écran reste du
 * markup.
 *
 * ⚠️ **Plus aucun filtrage en mémoire ici** (D24) : `selectInquiryList` a disparu avec les
 * fixtures. Ce qui reste est le schéma de l'URL — l'URL est l'état, un filtre se partage en
 * copiant l'adresse — et sa traduction en paramètres de requête.
 *
 * Chaque champ porte un `.catch` : un paramètre inattendu retombe sur son défaut au lieu de
 * casser l'écran (M21 appliqué à l'URL).
 */

/**
 * Une vue, en un seul endroit : son libellé, et **ce que le serveur sait en faire**.
 *
 * `served: false` : ni le filtre ni le compteur ne sont calculables, l'écran présente donc la vue
 * désactivée et sans compteur. `assignment` est le préréglage que la vue pose côté serveur — une
 * vue **n'est pas un concept**, c'est un préréglage de filtres (M14 § 1).
 */
interface ViewDefinition {
  value: string
  label: string
  served: boolean
  /** `undefined` plutôt qu'omis : présente sur chaque entrée, elle se lit sans narrower l'union. */
  assignment: InquiryListRequest['assignment']
  /**
   * Ce que la vue impose au filtre « RDV », ou `undefined` quand elle le laisse libre.
   *
   * ⚠️ **`to_plan` n'est pas `unscheduled`** : il y ajoute « et le contrôle est encore attendu ».
   * Sans cette moitié, « À planifier » proposerait de poser un créneau sur des dossiers archivés.
   */
  appointment: InquiryListRequest['appointment'] | undefined
}

/**
 * Les quatre onglets du prototype. **Tous servis depuis le 22/09/2026** : « À planifier » et
 * « Avec RDV » attendaient que la couture traduise leur question, que le serveur savait déjà
 * répondre (`viewConditions`).
 */
export const VIEWS = [
  // ⚠️ `undefined` et non `'all'` : ces deux vues ne **contraignent** pas le rendez-vous, elles
  // le laissent au sélecteur. Y écrire `'all'` ferait gagner la vue à tous les coups, et le
  // filtre « RDV » n'aurait aucun effet sous l'onglet « Toutes » — c'est-à-dire presque toujours.
  { value: 'all', label: 'Toutes', served: true, assignment: 'all', appointment: undefined },
  {
    value: 'to_schedule',
    label: 'À planifier',
    served: true,
    assignment: undefined,
    appointment: 'to_plan',
  },
  {
    value: 'with_appointment',
    label: 'Avec RDV',
    served: true,
    assignment: undefined,
    appointment: 'scheduled',
  },
  // « À affecter » et non le « Non attribuées » du prototype (#695) : tout sauf la vente et l'enquête
  // hors secteur s'affecte (décision de GHP du 04/11/2026), l'agent les planifie sur place — c'est ici, et non sous « À planifier », que le
  // bureau les trouve.
  {
    value: 'unassigned',
    label: 'À affecter',
    served: true,
    assignment: 'unassigned',
    appointment: undefined,
  },
] as const satisfies readonly ViewDefinition[]

/** La vue est **dérivée** de la table — jamais une seconde liste de littéraux à tenir. */
export type InquiryListView = (typeof VIEWS)[number]['value']

/**
 * ⚠️ Seules les vues servies entrent dans le schéma de l'URL — la garde reste, même si les quatre
 * le sont aujourd'hui : un onglet ajouté avant sa condition serveur afficherait tout le tableau
 * en promettant le contraire, et c'est ce `.catch` qui l'en empêche.
 */
const SERVED_VIEWS = VIEWS.filter((view) => view.served).map((view) => view.value) as [
  'all',
  'to_schedule',
  'with_appointment',
  'unassigned',
]

export const inquiryListSearchSchema = z.object({
  view: z.enum(SERVED_VIEWS).default('all').catch('all'),
  q: urlText(''),
  offset: urlCount(0),
  // L'équipe **part au serveur** (`teamId`), les cinq suivantes non.
  team: urlText('all'),
  // ⚠️ Les quatre filtres qui suivent restent dans l'URL parce que les listes déroulantes ont
  // besoin d'une valeur, mais **aucun ne part au serveur** — et ce qui leur manque n'est pas le
  // même pour tous : le client, sa nature (`Lot`/`Client`) et le RDV (`Appointment`) ont leur
  // table et attendent une condition côté API ; le type (`Inquiry.purpose`) a désormais sa
  // colonne (PR #288) mais l'API ne la sert pas encore. L'écran les présente désactivés — cf.
  // `UNSERVED_FILTERS`.
  client: urlText('all'),
  /** Le lot du client choisi (#505) — par identifiant, et oublié quand le client change. */
  lot: urlText('all'),
  clientKind: z
    .enum(['all', ...CLIENT_KIND_VALUES])
    .default('all')
    .catch('all'),
  kind: z
    .enum(['all', ...INQUIRY_KIND_VALUES])
    .default('all')
    .catch('all'),
  appointment: z.enum(['all', 'yes', 'no']).default('all').catch('all'),
  /**
   * Le tri — **la colonne et son sens**, tous deux dans l'URL comme les filtres.
   *
   * ⚠️ **Les valeurs sont celles du contrat, pas une seconde liste.** `INQUIRY_LIST_SORTS` est
   * l'énumération que le dépôt traduit en `ORDER BY` : la recopier ici laisserait l'URL proposer
   * une colonne que le serveur refuse, et le `.catch` la ferait retomber en silence sur le tri
   * par défaut — un en-tête qui s'allume sans rien changer à la liste.
   *
   * ⚠️ **Le tri est une clause serveur, jamais un `sort()` sur la page reçue** (D24) : trier
   * cinquante lignes sur six mille donnerait un ordre juste localement et faux globalement.
   *
   * Le défaut est celui du serveur — `createdAt` décroissant : la Réf. GHP est facultative (M1),
   * et PostgreSQL range les `NULL` en fin de tri ascendant, donc trier par référence enverrait au
   * fond de la dernière page les demandes qui viennent d'arriver.
   */
  sort: z.enum(INQUIRY_LIST_SORTS).default('createdAt').catch('createdAt'),
  direction: z.enum(INQUIRY_LIST_DIRECTIONS).default('desc').catch('desc'),
  /**
   * La demande dont on crée la **contre-visite** (#321) — « Archives » et le contrôle validé y
   * envoient. Présente, la boîte de création s'ouvre sur son brouillon ; elle ne filtre rien.
   */
  counterVisitOf: z.string().optional().catch(undefined),
})

export type InquiryListSearch = z.infer<typeof inquiryListSearchSchema>

/** Les valeurs par défaut du schéma, partagées entre l'URL et « Réinitialiser ». */
/**
 * L'URL du **tableau de bord** : le client, et rien d'autre (#661).
 *
 * ⚠️ Tolérant comme les listes (M21) : un identifiant périmé retombe sur « tous les clients »
 * plutôt que de vider l'écran de ses chiffres.
 */
export const dashboardSearchSchema = z.object({
  clientId: z
    .union([z.literal('all'), z.string().min(1)])
    .default('all')
    .catch('all'),
})

export type DashboardSearch = z.infer<typeof dashboardSearchSchema>

export const DASHBOARD_DEFAULTS: DashboardSearch = { clientId: 'all' }

export const INQUIRY_LIST_DEFAULTS: InquiryListSearch = inquiryListSearchSchema.parse({})

/**
 * Ce que chaque filtre non servi attend, nommé — pour que l'écran le dise à l'utilisateur au lieu
 * de le laisser deviner devant un contrôle inerte.
 */
/**
 * Les filtres que l'URL porte et que le serveur **n'applique pas**. Vide depuis le 22/09/2026 :
 * les quatre derniers — client, nature du client, type de contrôle, rendez-vous — ont reçu leur
 * condition serveur, les trois premiers parce que le modèle porte enfin `Inquiry.client_id` et
 * `Inquiry.purpose` (M32), le dernier parce qu'il était déjà servi et qu'il ne manquait que
 * cette traduction.
 *
 * ⚠️ **On ne supprime pas la table pour autant** : un filtre qui n'est pas appliqué se dit à
 * l'écran (le sélecteur se désarme et donne sa raison) plutôt que de faire semblant. Le jour où
 * le prototype en montre un que le modèle ne porte pas, il s'ajoute ici.
 */
export const UNSERVED_FILTERS = {} as const satisfies Partial<
  Record<keyof InquiryListSearch, string>
>

/** L'URL de l'écran dit oui/non, le contrat dit planifié/non planifié. */
const APPOINTMENT_QUERY = {
  all: 'all',
  yes: 'scheduled',
  no: 'unscheduled',
} as const satisfies Record<InquiryListSearch['appointment'], InquiryListRequest['appointment']>

/**
 * Combien de lignes une page demande. Le prototype affiche un tableau dense ; cinquante lignes
 * tiennent dans une zone défilante sans que la pagination devienne le geste principal.
 */
export const INQUIRY_PAGE_SIZE = 50

/**
 * **La couture** : l'état d'URL de l'écran vers ce que l'API accepte (D21).
 *
 * C'est ici — et nulle part ailleurs — que vit l'écart entre ce que le prototype montre et ce que
 * le modèle porte. Un filtre absent de cette fonction est un filtre que le serveur n'applique
 * pas : le voir d'un coup d'œil est tout l'intérêt de le rassembler ici.
 */
export function toInquiryListQuery(search: InquiryListSearch): InquiryListRequest {
  const view = VIEWS.find(({ value }) => value === search.view)

  return {
    search: search.q.trim(),
    // Le paramètre d'URL s'appelle `team`, celui du serveur `teamId` : c'est un identifiant, et le
    // contrat le nomme pour ce qu'il est (M22). La traduction vit ici, avec les autres.
    teamId: search.team,
    // Le donneur d'ordre et sa famille : l'URL les nomme comme le prototype nomme ses filtres,
    // le contrat comme il nomme ses colonnes.
    clientId: search.client,
    lotId: search.lot,
    clientFamily: search.clientKind,
    purpose: search.kind,
    /**
     * ⚠️ **L'onglet l'emporte sur le filtre**, et c'est ce que l'écran montre : « À planifier »
     * et « Avec RDV » sont des vues, le sélecteur « RDV » est un filtre, et les deux portent la
     * même question. Laisser le second écraser le premier ferait que cliquer « Avec RDV »
     * n'aurait aucun effet tant qu'un filtre contraire traîne dans l'URL.
     *
     * `to_plan` n'est pas `unscheduled` : la vue y ajoute « et le contrôle est encore attendu ».
     */
    appointment: view?.appointment ?? APPOINTMENT_QUERY[search.appointment],
    assignment: view?.assignment ?? 'all',
    sort: search.sort,
    direction: search.direction,
    limit: INQUIRY_PAGE_SIZE,
    offset: search.offset,
  }
}

/**
 * La couture de l'export : les **mêmes** filtres que la liste, sans la page.
 *
 * ⚠️ C'est volontairement dérivé de `toInquiryListQuery`, pas recopié. Un filtre ajouté à la
 * liste et oublié ici rendrait un fichier plus large (ou plus étroit) que le tableau, sans
 * que rien ne le signale — exactement le défaut que l'issue #256 nomme. `limit` / `offset`
 * n'en font pas partie : ce sont les bornes de l'écran, pas de l'ensemble.
 */
export function toInquiryExportQuery(search: InquiryListSearch): InquiryListRequest {
  const { limit: _limit, offset: _offset, ...filters } = toInquiryListQuery(search)

  return filters
}
